import { Directive, signal } from '@angular/core';

import { SbDisclosureFlyoutGroup, type SbDisclosureFlyoutTrigger } from './disclosure-flyout.tokens';

/**
 * Keeps one disclosure expanded at a time among its descendants, and tells each trigger whether a
 * sibling is already open — which is what lets hover use a longer delay for a swap than for a first
 * open. Optional: a trigger with no group ancestor opens and closes on its own.
 *
 * Membership follows the element injector, which a panel's projected content does not escape — so
 * a trigger placed *inside* another disclosure's panel joins the same group, and opening it
 * collapses the panel it lives in. Nested disclosures are unsupported until a consumer needs them.
 */
@Directive({
  selector: '[sbDisclosureFlyoutGroup]',
  providers: [{ provide: SbDisclosureFlyoutGroup, useExisting: DisclosureFlyoutGroupDirective }],
})
export class DisclosureFlyoutGroupDirective implements SbDisclosureFlyoutGroup {
  #expandedTrigger = signal<SbDisclosureFlyoutTrigger | undefined>(undefined);

  expandedTrigger = this.#expandedTrigger.asReadonly();

  claimExpanded(trigger: SbDisclosureFlyoutTrigger): void {
    const previous = this.#expandedTrigger();

    if (previous && previous !== trigger) {
      previous.collapse();
    }

    this.#expandedTrigger.set(trigger);
  }

  releaseExpanded(trigger: SbDisclosureFlyoutTrigger): void {
    if (this.#expandedTrigger() === trigger) {
      this.#expandedTrigger.set(undefined);
    }
  }
}
